/* global Excel */
import { removeDiffRow, upsertDiffRow } from "../../slices/diffSlice";
import store from "../../store/store";
import { fetchSingleProduct } from "../magento/adobeCommerce";
import { shallowObjectMatch } from "../common";
import { buildMagentoPayload, toComparable, hasRowDiff, normalizeHeader, mapHeaderToProductKey } from "./utils";
import { DIFF_COLOR, BLOCK_COLOR } from "./dom";

const pendingEdits = new Map<string, NodeJS.Timeout>();

export async function onChangeHandler(eventArgs: Excel.WorksheetChangedEventArgs) {
  console.log("onChangeHandler fired", eventArgs);
  // Exit early if the Excel cell value did not actually change
  if (
    eventArgs?.details?.valueBefore !== undefined &&
    eventArgs?.details?.valueBefore === eventArgs?.details?.valueAfter
  ) {
    console.log("Exiting early: value did not change");
    return;
  }

  const sheetId = (eventArgs as any).worksheetId as string;
  const address = eventArgs.address;
  const key = `${sheetId}-${address}`;
  console.log("Processing change for key:", key);

  if (pendingEdits.has(key)) {
    console.log("Clearing pending edit for key:", key);
    clearTimeout(pendingEdits.get(key));
  }

  pendingEdits.set(
    key,
    setTimeout(async () => {
      pendingEdits.delete(key);
      console.log("Executing delayed edit for key:", key);
      try {
        await Excel.run(async (context) => {
          const reduxStoreState = store.getState();
          const sheet = context.workbook.worksheets.getItemOrNullObject(sheetId);

          sheet.load("name");
          await context.sync();
          console.log("Loaded sheet name:", sheet.name);

          if (sheet.isNullObject) return;

          const changedRange = sheet.getRange(address);
          changedRange.load("rowIndex");
          await context.sync();
          console.log("Loaded rowIndex:", changedRange.rowIndex);

          const usedRange = sheet.getUsedRangeOrNullObject(true);
          usedRange.load(["columnCount"]);
          await context.sync();
          console.log("Loaded columnCount:", usedRange.columnCount);

          if (usedRange.isNullObject || usedRange.columnCount === 0) return;
          if (changedRange.rowIndex === 0) return;

          const headerRange = sheet.getRangeByIndexes(0, 0, 1, usedRange.columnCount);
          const rowRange = sheet.getRangeByIndexes(changedRange.rowIndex, 0, 1, usedRange.columnCount);

          headerRange.load("values");
          rowRange.load("values");
          await context.sync();

          // Values
          const headers = headerRange.values[0] as string[];
          const rowValues = rowRange.values[0];
          const formattedExcelRowData = buildMagentoPayload(headers, rowValues);
          console.log("Formatted row data:", formattedExcelRowData);

          // Initial Product Data
          const initialProductData =
            reduxStoreState.products.items.find(
              (product) => toComparable(product?.sku) === toComparable(formattedExcelRowData?.sku)
            ) || null;

          const activeAccount =
            reduxStoreState.accounts.accounts.find(
              (account) => account.id === reduxStoreState.accounts.activeAccountId
            ) || null;

          if (!formattedExcelRowData?.sku || !initialProductData?.sku || !activeAccount) {
            throw new Error("Missing data for processing the change.");
          }

          // Compare if current excel row data is same as inital product data
          const isRowChanged = hasRowDiff(headers, rowValues, initialProductData ? [initialProductData] : []);

          if (!isRowChanged) {
            rowRange.format.fill.color = "#FFFFFF";
            store.dispatch(removeDiffRow({ sheetId, rowIndex: changedRange.rowIndex }));
            return;
          }

          // Determine whether the API version is still the same as our Initial version
          const apiProductData = await fetchSingleProduct(activeAccount, toComparable(formattedExcelRowData.sku));
          
          const isApiUnchanged = headers.every((header) => {
            const key = normalizeHeader(header);
            if (!key) return true;
            const productKey = mapHeaderToProductKey(key);
            const initialVal = toComparable((initialProductData as any)?.[productKey]);
            const apiVal = toComparable(apiProductData?.[productKey]);
            return initialVal === apiVal;
          });

          if (isApiUnchanged) {
            rowRange.format.fill.color = DIFF_COLOR;
            store.dispatch(
              upsertDiffRow({
                sheetId,
                rowIndex: changedRange.rowIndex,
                data: formattedExcelRowData,
              })
            );
          } else {
            rowRange.format.fill.color = BLOCK_COLOR;
            store.dispatch(
              upsertDiffRow({
                sheetId,
                rowIndex: changedRange.rowIndex,
                data: formattedExcelRowData,
                isOutOfSync: true,
              })
            );
          }
        });
      } catch (err) {
        console.error("onChangeHandler error:", err);
      }
    }, 500)
  );
}
